import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { home } from "../content/home";
import { cn } from "../lib/cn";

const { label, headline, problem, facts, hint } = home.stats;

// Each stat gets its own tilt, so moving on swings the tablet and its blue block.
const TABLET_TILT = ["rotate-2", "-rotate-2", "rotate-1"];
const BLOCK_TILT = ["-rotate-6", "rotate-[5deg]", "-rotate-3"];

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper";

export function StatsSection() {
  const [index, setIndex] = useState(0);
  const stat = problem[index];
  const next = () => setIndex((i) => (i + 1) % problem.length);

  return (
    <section id="why" aria-label={label} className="scroll-mt-20 px-4 py-24 sm:px-6 lg:py-28">
      <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <div>
          <h2 className="font-serif text-[clamp(2.25rem,4.5vw,3.5rem)] font-medium leading-[1.02] tracking-[-0.015em] text-char">
            {headline.line1}
            <br />
            <em className="text-brand-500">{headline.line2}</em>
          </h2>

          <ul aria-label="With Intakall" className="mt-10 space-y-5 border-t border-char/20 pt-8">
            {facts.map((fact) => (
              <li key={fact.value} className="flex items-baseline gap-5">
                <span className="w-10 shrink-0 font-serif text-[44px] leading-none text-brand-500">{fact.value}</span>
                <span className="font-sans text-base leading-relaxed text-char/75">{fact.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mx-auto w-full max-w-[520px]">
          <div className="relative px-3 py-6 sm:px-6">
            <div
              aria-hidden="true"
              className={cn(
                "absolute inset-x-0 inset-y-8 rounded-[28px] border border-char bg-brand-500 motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                BLOCK_TILT[index],
              )}
            />
            <div
              role="group"
              aria-label={`Statistic ${index + 1} of ${problem.length}`}
              onClick={next}
              className={cn(
                "relative cursor-pointer rounded-[26px] border border-char bg-char p-2.5 shadow-offset motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-[cubic-bezier(0.34,1.56,0.64,1)] sm:p-3.5",
                TABLET_TILT[index],
              )}
            >
              <div className="flex aspect-[16/11] flex-col justify-center rounded-[18px] bg-paper-raised px-8 sm:px-10">
                <div key={index} className="motion-safe:animate-screen-in">
                  <p className="font-serif text-[clamp(3.5rem,8vw,4.75rem)] leading-none text-char">{stat.value}</p>
                  <p className="mt-4 max-w-sm font-sans text-lg leading-snug text-char/75">{stat.label}</p>
                  <a
                    href={stat.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                      "mt-4 inline-block rounded font-mono text-[11px] text-char/65 underline decoration-char/20 underline-offset-4 hover:text-char",
                      focusRing,
                    )}
                  >
                    {stat.source}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-4">
            <div className="flex gap-2">
              {problem.map((p, i) => (
                <button
                  key={p.value}
                  type="button"
                  aria-label={`Show statistic ${i + 1}`}
                  aria-pressed={i === index}
                  onClick={() => setIndex(i)}
                  className={cn(
                    "h-2.5 rounded-full transition-all duration-300",
                    i === index ? "w-7 bg-char" : "w-2.5 bg-char/25 hover:bg-char/45",
                    focusRing,
                  )}
                />
              ))}
            </div>
            <button
              type="button"
              aria-label="Next statistic"
              onClick={next}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full border border-char bg-paper-raised shadow-offset-sm transition-transform hover:-translate-x-px hover:translate-y-px",
                focusRing,
              )}
            >
              <ArrowRight className="h-4 w-4 text-char" aria-hidden="true" />
            </button>
            <span className="font-hand text-xl text-char/60">{hint}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
